import { useState } from 'react';
import type { Dispatch, FC, FormEventHandler, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../lib/format';

export interface SelfEmployee {
  id?: number | string;
  full_name: string;
  employee_code: string;
  department?: string | null;
  position?: string | null;
  account_holder_name?: string | null;
  payout_method?: string | null;
  bank_id?: number | string | null;
  bank_name?: string | null;
  bank_account_number?: string | null;
  bank_branch_id?: number | string | null;
  bank_branch?: string | null;
  mobile_wallet_number?: string | null;
  autopay_enabled?: boolean;
  basic_salary: number;
  leave_balance_days: number | string;
}

export interface SelfBank {
  id: number | string;
  code: string;
  name: string;
  short_name?: string | null;
  swift_code?: string | null;
  supports_interbank: boolean;
}

export interface SelfBankBranch {
  id: number | string;
  bank_id: number | string;
  code: string;
  name: string;
  city?: string | null;
}

export interface SelfBankingFormState {
  account_holder_name: string;
  payout_method: string;
  bank_id: string;
  bank_branch_id: string;
  bank_account_number: string;
  mobile_wallet_number: string;
  autopay_enabled: boolean;
}

export interface SelfBenefit {
  id: number | string;
  benefit_name: string;
  amount: number;
}

export interface SelfProfile {
  employee: SelfEmployee;
  benefits?: SelfBenefit[];
  onboarding_handoff?: {
    id: number | string;
    requisition_title: string;
    status: string;
    target_start_date?: string | null;
  } | null;
  onboarding_tasks?: Array<{
    id: number | string;
    title: string;
    is_required?: boolean;
    acknowledgement_statement?: string | null;
    acknowledgement_version?: string | null;
    status: string;
    due_date?: string | null;
    acknowledged_at?: string | null;
    acknowledged_signature_name?: string | null;
  }>;
  onboarding_documents?: Array<{
    id: number | string;
    document_name: string;
    file_name: string;
    created_at: string;
  }>;
  training_assignments?: Array<{
    id: number | string;
    course_title: string;
    course_category: string;
    status: string;
    due_date?: string | null;
    completed_at?: string | null;
    acknowledged_signature_name?: string | null;
  }>;
  employee_documents?: Array<{
    id: number | string;
    category: string;
    document_name: string;
    file_name: string;
    expires_at?: string | null;
    status: string;
    requires_acknowledgement: boolean;
    acknowledgement_statement?: string | null;
    acknowledgement_version?: string | null;
    acknowledged_at?: string | null;
    acknowledged_signature_name?: string | null;
  }>;
}

export interface SelfCurrentUser {
  mfa_enabled?: boolean;
}

export interface SelfProfileCardProps {
  profile: SelfProfile | null | undefined;
  currentUser: SelfCurrentUser | null | undefined;
  activeLanguage: string;
  banks: SelfBank[];
  bankBranches: SelfBankBranch[];
  bankingForm: SelfBankingFormState;
  setBankingForm: Dispatch<SetStateAction<SelfBankingFormState>>;
  onBankingSubmit: FormEventHandler<HTMLFormElement>;
  onEnableMfa: () => void;
  onOnboardingDocumentDownload: (documentId: number | string, fileName: string) => void;
  onOnboardingTaskComplete: (taskId: number | string, signatureName?: string) => void;
  onTrainingComplete: (assignmentId: number | string, signatureName: string) => void | Promise<void>;
  onEmployeeDocumentAcknowledge: (documentId: number | string, signatureName: string) => void | Promise<void>;
  onEmployeeDocumentDownload: (documentId: number | string, fileName: string) => void | Promise<void>;
  onOnboardingDocumentUpload: FormEventHandler<HTMLFormElement>;
  selfOnboardingDocumentForm: {
    document_name: string;
    task_id: string;
    file: File | null;
  };
  setSelfOnboardingDocumentForm: Dispatch<SetStateAction<{
    document_name: string;
    task_id: string;
    file: File | null;
  }>>;
  isBankingSubmitting: boolean;
  isSelfOnboardingSubmitting: boolean;
  isMfaSubmitting: boolean;
  error?: string | null;
}

export const SelfProfileCard: FC<SelfProfileCardProps> = ({
  profile,
  currentUser,
  activeLanguage,
  banks,
  bankBranches,
  bankingForm,
  setBankingForm,
  onBankingSubmit,
  onEnableMfa,
  onOnboardingDocumentDownload,
  onOnboardingTaskComplete,
  onTrainingComplete,
  onEmployeeDocumentAcknowledge,
  onEmployeeDocumentDownload,
  onOnboardingDocumentUpload,
  selfOnboardingDocumentForm,
  setSelfOnboardingDocumentForm,
  isBankingSubmitting,
  isSelfOnboardingSubmitting,
  isMfaSubmitting,
  error,
}) => {
  const { t } = useTranslation();
  const [signatureInputs, setSignatureInputs] = useState<Record<string, string>>({});
  const [trainingSignatures, setTrainingSignatures] = useState<Record<string, string>>({});
  const [documentSignatures, setDocumentSignatures] = useState<Record<string, string>>({});
  const bankTransfers = bankingForm.payout_method !== 'mobile_money';

  return (
    <article className="card">
      <h2>{t('selfServiceTitle')}</h2>
      {profile ? (
        <dl>
          <div>
            <dt>{t('employee')}</dt>
            <dd>{profile.employee.full_name}</dd>
          </div>
          <div>
            <dt>{t('employeeCode')}</dt>
            <dd>{profile.employee.employee_code}</dd>
          </div>
          <div>
            <dt>{t('department')}</dt>
            <dd>{profile.employee.department || t('notAvailable')}</dd>
          </div>
          <div>
            <dt>{t('position')}</dt>
            <dd>{profile.employee.position || t('notAvailable')}</dd>
          </div>
          <div>
            <dt>{t('basicSalary')}</dt>
            <dd>{formatCurrency(profile.employee.basic_salary, activeLanguage)}</dd>
          </div>
          <div>
            <dt>{t('leaveBalance')}</dt>
            <dd>{profile.employee.leave_balance_days}</dd>
          </div>
          <div>
            <dt>{t('mfaEnabled')}</dt>
            <dd>{profile.employee && currentUser?.mfa_enabled ? t('yes') : t('no')}</dd>
          </div>
        </dl>
      ) : (
        <p>{t('noLinkedEmployee')}</p>
      )}
      {profile ? (
        <form className="subcard" onSubmit={onBankingSubmit}>
          <h3>Banking and payout preferences</h3>
          <p className="helper">Choose your receiving bank from the approved directory so payroll payouts can be routed correctly.</p>
          <div className="split">
            <label>
              {t('accountHolderName')}
              <input
                value={bankingForm.account_holder_name}
                onChange={(event) => setBankingForm((current) => ({ ...current, account_holder_name: event.target.value }))}
              />
            </label>
            <label>
              {t('payoutMethod')}
              <select
                value={bankingForm.payout_method}
                onChange={(event) =>
                  setBankingForm((current) => ({
                    ...current,
                    payout_method: event.target.value,
                    bank_id: event.target.value === 'mobile_money' ? '' : current.bank_id,
                    bank_branch_id: event.target.value === 'mobile_money' ? '' : current.bank_branch_id,
                    bank_account_number: event.target.value === 'mobile_money' ? '' : current.bank_account_number,
                    mobile_wallet_number: event.target.value === 'mobile_money' ? current.mobile_wallet_number : '',
                  }))
                }
              >
                <option value="bank_transfer">{t('bankTransfer')}</option>
                <option value="mobile_money">{t('mobileMoney')}</option>
              </select>
            </label>
          </div>

          {bankTransfers ? (
            <>
              <div className="split">
                <label>
                  {t('bankName')}
                  <select
                    value={bankingForm.bank_id}
                    onChange={(event) =>
                      setBankingForm((current) => ({
                        ...current,
                        bank_id: event.target.value,
                        bank_branch_id: '',
                      }))
                    }
                  >
                    <option value="">Select bank</option>
                    {banks.map((bank) => (
                      <option key={bank.id} value={bank.id}>
                        {bank.name} ({bank.code})
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('bankBranch')}
                  <select
                    value={bankingForm.bank_branch_id}
                    onChange={(event) => setBankingForm((current) => ({ ...current, bank_branch_id: event.target.value }))}
                    disabled={!bankingForm.bank_id}
                  >
                    <option value="">Select branch</option>
                    {bankBranches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} ({branch.code})
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label>
                {t('bankAccountNumber')}
                <input
                  value={bankingForm.bank_account_number}
                  onChange={(event) => setBankingForm((current) => ({ ...current, bank_account_number: event.target.value }))}
                />
              </label>
            </>
          ) : (
            <label>
              {t('mobileWalletNumber')}
              <input
                value={bankingForm.mobile_wallet_number}
                onChange={(event) => setBankingForm((current) => ({ ...current, mobile_wallet_number: event.target.value }))}
              />
            </label>
          )}

          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={bankingForm.autopay_enabled}
              onChange={(event) => setBankingForm((current) => ({ ...current, autopay_enabled: event.target.checked }))}
            />
            <span>Allow payroll to submit payouts automatically after approval.</span>
          </label>

          <button type="submit" disabled={isBankingSubmitting}>
            {isBankingSubmitting ? 'Saving...' : 'Save payout details'}
          </button>
        </form>
      ) : null}
      {profile?.benefits?.length ? (
        <ul className="list compact-list">
          {profile.benefits.map((benefit) => (
            <li key={benefit.id}>
              <div>
                <strong>{benefit.benefit_name}</strong>
                <span>{formatCurrency(benefit.amount, activeLanguage)}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>{t('noBenefits')}</p>
      )}
      {profile?.onboarding_handoff ? (
        <>
          <h3>Onboarding progress</h3>
          <p>
            {profile.onboarding_handoff.requisition_title}
            {` • ${profile.onboarding_handoff.status}`}
            {profile.onboarding_handoff.target_start_date ? ` • target ${profile.onboarding_handoff.target_start_date}` : ''}
          </p>
          {profile.onboarding_tasks?.length ? (
            <ul className="list compact-list">
              {profile.onboarding_tasks.map((task) => (
                <li key={task.id}>
                  <div>
                    <strong>{task.title}</strong>
                    <span>
                      {task.is_required ? 'requires acknowledgement • ' : ''}
                      {task.status}
                      {task.due_date ? ` • due ${task.due_date}` : ''}
                      {task.acknowledgement_version ? ` • policy ${task.acknowledgement_version}` : ''}
                      {task.acknowledged_at ? ` • acknowledged ${task.acknowledged_at}` : ''}
                      {task.acknowledged_signature_name ? ` • signed ${task.acknowledged_signature_name}` : ''}
                    </span>
                    {task.is_required && task.acknowledgement_statement ? <span>{task.acknowledgement_statement}</span> : null}
                  </div>
                  {task.status !== 'completed' ? (
                    <div className="button-row">
                      {task.is_required ? (
                        <input
                          placeholder={profile.employee.full_name}
                          value={signatureInputs[String(task.id)] || ''}
                          onChange={(event) => setSignatureInputs((current) => ({ ...current, [String(task.id)]: event.target.value }))}
                        />
                      ) : null}
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => onOnboardingTaskComplete(task.id, signatureInputs[String(task.id)] || '')}
                        disabled={isSelfOnboardingSubmitting}
                      >
                        {task.is_required ? 'Acknowledge and complete' : 'Mark complete'}
                      </button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p>No onboarding tasks assigned yet.</p>
          )}
          <form className="subcard" onSubmit={onOnboardingDocumentUpload}>
            <h3>Upload onboarding document</h3>
            <div className="split">
              <label>
                Document name
                <input
                  value={selfOnboardingDocumentForm.document_name}
                  onChange={(event) => setSelfOnboardingDocumentForm((current) => ({ ...current, document_name: event.target.value }))}
                />
              </label>
              <label>
                Related task
                <select
                  value={selfOnboardingDocumentForm.task_id}
                  onChange={(event) => setSelfOnboardingDocumentForm((current) => ({ ...current, task_id: event.target.value }))}
                >
                  <option value="">General document</option>
                  {profile.onboarding_tasks?.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              File
              <input
                type="file"
                onChange={(event) => setSelfOnboardingDocumentForm((current) => ({ ...current, file: event.target.files?.[0] || null }))}
              />
            </label>
            <button type="submit" disabled={isSelfOnboardingSubmitting}>
              {isSelfOnboardingSubmitting ? 'Uploading…' : 'Upload document'}
            </button>
          </form>
          {profile.onboarding_documents?.length ? (
            <ul className="list compact-list">
              {profile.onboarding_documents.map((document) => (
                <li key={document.id}>
                  <div>
                    <strong>{document.document_name}</strong>
                    <span>{document.file_name}</span>
                  </div>
                  <div className="button-row">
                    <button type="button" className="secondary-button" onClick={() => onOnboardingDocumentDownload(document.id, document.file_name)}>
                      Download
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p>No onboarding documents available yet.</p>
          )}
        </>
      ) : null}
      {profile?.training_assignments?.length ? (
        <>
          <h3>My Learning</h3>
          <ul className="list compact-list">
            {profile.training_assignments.map((assignment) => (
              <li key={assignment.id}>
                <div>
                  <strong>{assignment.course_title}</strong>
                  <span>
                    {assignment.course_category} • {assignment.status}
                    {assignment.due_date ? ` • due ${assignment.due_date}` : ''}
                    {assignment.completed_at ? ` • completed ${assignment.completed_at}` : ''}
                    {assignment.acknowledged_signature_name ? ` • signed ${assignment.acknowledged_signature_name}` : ''}
                  </span>
                </div>
                {assignment.status !== 'completed' ? (
                  <div className="button-row">
                    <input
                      placeholder={profile.employee.full_name}
                      value={trainingSignatures[String(assignment.id)] || ''}
                      onChange={(event) => setTrainingSignatures((current) => ({ ...current, [String(assignment.id)]: event.target.value }))}
                    />
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={isSelfOnboardingSubmitting}
                      onClick={() => void onTrainingComplete(assignment.id, trainingSignatures[String(assignment.id)] || profile.employee.full_name)}
                    >
                      Complete and acknowledge
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {profile?.employee_documents?.length ? (
        <>
          <h3>My Documents</h3>
          <ul className="list compact-list">
            {profile.employee_documents.map((document) => (
              <li key={document.id}>
                <div>
                  <strong>{document.document_name}</strong>
                  <span>
                    {document.category} • {document.status}
                    {document.expires_at ? ` • expires ${document.expires_at}` : ''}
                    {document.requires_acknowledgement ? ` • ${document.acknowledged_at ? 'acknowledged' : 'pending acknowledgement'}` : ''}
                    {document.acknowledgement_version ? ` • policy ${document.acknowledgement_version}` : ''}
                  </span>
                  {document.acknowledgement_statement ? <span>{document.acknowledgement_statement}</span> : null}
                </div>
                <div className="button-row">
                  <button type="button" className="secondary-button" onClick={() => void onEmployeeDocumentDownload(document.id, document.file_name)}>
                    Download
                  </button>
                  {document.requires_acknowledgement && !document.acknowledged_at ? (
                    <>
                      <input
                        placeholder={profile.employee.full_name}
                        value={documentSignatures[String(document.id)] || ''}
                        onChange={(event) => setDocumentSignatures((current) => ({ ...current, [String(document.id)]: event.target.value }))}
                      />
                      <button
                        type="button"
                        className="secondary-button"
                        disabled={isSelfOnboardingSubmitting}
                        onClick={() => void onEmployeeDocumentAcknowledge(document.id, documentSignatures[String(document.id)] || profile.employee.full_name)}
                      >
                        Acknowledge
                      </button>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {!currentUser?.mfa_enabled ? (
        <button type="button" className="secondary-button" onClick={onEnableMfa} disabled={isMfaSubmitting}>
          {isMfaSubmitting ? t('saving') : t('enableMfa')}
        </button>
      ) : null}
      {error ? <p className="error">{error}</p> : null}
    </article>
  );
};

export interface SelfLeaveFormState {
  leave_type_id: string;
  start_date: string;
  end_date: string;
  reason: string;
}

export interface SelfLeaveType {
  id: number | string;
  name: string;
}

export interface SelfLeaveRequestCardProps {
  form: SelfLeaveFormState;
  setForm: Dispatch<SetStateAction<SelfLeaveFormState>>;
  leaveTypes: SelfLeaveType[];
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
}

export const SelfLeaveRequestCard: FC<SelfLeaveRequestCardProps> = ({
  form,
  setForm,
  leaveTypes,
  isSubmitting,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>{t('requestLeave')}</h2>
      <label>
        {t('leaveType')}
        <select
          value={form.leave_type_id}
          onChange={(event) => setForm((current) => ({ ...current, leave_type_id: event.target.value }))}
        >
          <option value="">{t('selectLeaveType')}</option>
          {leaveTypes.map((leaveType) => (
            <option key={leaveType.id} value={leaveType.id}>
              {leaveType.name}
            </option>
          ))}
        </select>
      </label>
      <div className="split">
        <label>
          {t('startDate')}
          <input
            value={form.start_date}
            onChange={(event) => setForm((current) => ({ ...current, start_date: event.target.value }))}
          />
        </label>
        <label>
          {t('endDate')}
          <input
            value={form.end_date}
            onChange={(event) => setForm((current) => ({ ...current, end_date: event.target.value }))}
          />
        </label>
      </div>
      <label>
        {t('reason')}
        <input
          value={form.reason}
          onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
        />
      </label>
      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('saving') : t('submitLeaveRequest')}
      </button>
    </form>
  );
};

export interface SelfLeaveRequest {
  id: number | string;
  leave_type_name: string;
  start_date: string;
  end_date: string;
  status: string;
}

export interface MyLeaveRequestsCardProps {
  requests: SelfLeaveRequest[];
}

export const MyLeaveRequestsCard: FC<MyLeaveRequestsCardProps> = ({ requests }) => {
  const { t } = useTranslation();

  return (
    <article className="card list-card">
      <h2>{t('myLeaveRequests')}</h2>
      {requests.length ? (
        <ul className="list">
          {requests.map((leaveRequest) => (
            <li key={leaveRequest.id}>
              <div>
                <strong>{leaveRequest.leave_type_name}</strong>
                <span>{leaveRequest.start_date} to {leaveRequest.end_date} • {leaveRequest.status}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>{t('noLeaveRequests')}</p>
      )}
    </article>
  );
};

export interface SelfAttendanceRecord {
  id: number | string;
  work_date: string;
  clock_in?: string | null;
  clock_out?: string | null;
  regular_hours: number | string;
  overtime_hours: number | string;
  status: string;
}

export interface SelfAttendanceCardProps {
  attendance: SelfAttendanceRecord[];
  onClockIn: () => void | Promise<void>;
  onClockOut: () => void | Promise<void>;
  isSubmitting: boolean;
}

export const SelfAttendanceCard: FC<SelfAttendanceCardProps> = ({
  attendance,
  onClockIn,
  onClockOut,
  isSubmitting,
}) => {
  const openSession = attendance.find((record) => record.status === 'pending' && !record.clock_out);

  return (
    <article className="card list-card">
      <div className="section-header">
        <h2>My Attendance</h2>
        <span className="helper">{openSession ? 'Clocked in' : 'Ready'}</span>
      </div>
      <div className="button-row">
        <button type="button" onClick={() => void onClockIn()} disabled={isSubmitting || Boolean(openSession)}>
          {isSubmitting ? 'Saving...' : 'Clock in'}
        </button>
        <button type="button" className="secondary-button" onClick={() => void onClockOut()} disabled={isSubmitting || !openSession}>
          {isSubmitting ? 'Saving...' : 'Clock out'}
        </button>
      </div>
      {attendance.length ? (
        <ul className="list compact-list">
          {attendance.slice(0, 8).map((record) => (
            <li key={record.id}>
              <div>
                <strong>{record.work_date}</strong>
                <span>
                  {record.clock_in ? `In ${new Date(record.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'No clock-in'}
                  {' • '}
                  {record.clock_out ? `Out ${new Date(record.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Open'}
                  {' • '}
                  {Number(record.regular_hours).toFixed(2)}h regular • {Number(record.overtime_hours).toFixed(2)}h OT • {record.status}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>No attendance records yet.</p>
      )}
    </article>
  );
};

export interface SelfPerformanceGoal {
  id: number | string;
  cycle_name?: string | null;
  title: string;
  metric?: string | null;
  target_value?: string | null;
  weight: number | string;
  progress: number | string;
  status: string;
  due_date?: string | null;
}

export interface SelfPerformanceReview {
  id: number | string;
  cycle_name: string;
  overall_rating?: number | string | null;
  manager_comments?: string | null;
  employee_comments?: string | null;
  status: string;
  submitted_at?: string | null;
  acknowledged_at?: string | null;
}

export interface MyPerformanceCardProps {
  goals: SelfPerformanceGoal[];
  reviews: SelfPerformanceReview[];
  onGoalProgress: (goalId: number | string, progress: number, status: string) => void | Promise<void>;
  onReviewAcknowledge: (reviewId: number | string, employeeComments: string) => void | Promise<void>;
  isSubmitting: boolean;
}

export const MyPerformanceCard: FC<MyPerformanceCardProps> = ({
  goals,
  reviews,
  onGoalProgress,
  onReviewAcknowledge,
  isSubmitting,
}) => {
  const [goalInputs, setGoalInputs] = useState<Record<string, { progress: string; status: string }>>({});
  const [reviewComments, setReviewComments] = useState<Record<string, string>>({});

  return (
    <article className="card list-card">
      <div className="section-header">
        <h2>My Performance</h2>
        <span className="helper">{goals.length} goals</span>
      </div>
      {goals.length ? (
        <ul className="list compact-list">
          {goals.map((goal) => {
            const input = goalInputs[String(goal.id)] || { progress: String(goal.progress ?? 0), status: goal.status || 'in_progress' };
            return (
              <li key={goal.id}>
                <div>
                  <strong>{goal.title}</strong>
                  <span>
                    {goal.cycle_name || 'No cycle'} • {goal.metric || 'Goal'} {goal.target_value ? `target ${goal.target_value}` : ''} •{' '}
                    {Number(goal.weight).toFixed(0)}% weight • {Number(goal.progress).toFixed(0)}% • {goal.status}
                    {goal.due_date ? ` • due ${goal.due_date}` : ''}
                  </span>
                </div>
                <div className="button-row">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={input.progress}
                    onChange={(event) => setGoalInputs((current) => ({
                      ...current,
                      [String(goal.id)]: { ...input, progress: event.target.value },
                    }))}
                  />
                  <select
                    value={input.status}
                    onChange={(event) => setGoalInputs((current) => ({
                      ...current,
                      [String(goal.id)]: { ...input, status: event.target.value },
                    }))}
                  >
                    <option value="draft">Draft</option>
                    <option value="in_progress">In progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={isSubmitting}
                    onClick={() => void onGoalProgress(goal.id, Number(input.progress || 0), input.status)}
                  >
                    {isSubmitting ? 'Saving...' : 'Update'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p>No performance goals assigned yet.</p>
      )}

      <h3>Reviews</h3>
      {reviews.length ? (
        <ul className="list compact-list">
          {reviews.map((review) => (
            <li key={review.id}>
              <div>
                <strong>{review.cycle_name}</strong>
                <span>
                  Rating {review.overall_rating ?? 'not rated'} • {review.status}
                  {review.submitted_at ? ` • submitted ${review.submitted_at}` : ''}
                  {review.acknowledged_at ? ` • acknowledged ${review.acknowledged_at}` : ''}
                </span>
                {review.manager_comments ? <span>{review.manager_comments}</span> : null}
                {review.employee_comments ? <span>Your comments: {review.employee_comments}</span> : null}
              </div>
              {review.status === 'submitted' ? (
                <div className="button-row">
                  <input
                    placeholder="Optional acknowledgement comment"
                    value={reviewComments[String(review.id)] || ''}
                    onChange={(event) => setReviewComments((current) => ({ ...current, [String(review.id)]: event.target.value }))}
                  />
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={isSubmitting}
                    onClick={() => void onReviewAcknowledge(review.id, reviewComments[String(review.id)] || '')}
                  >
                    Acknowledge
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p>No performance reviews published yet.</p>
      )}
    </article>
  );
};

export interface SelfPayrollHistoryItem {
  id: number | string;
  payroll_month?: string | null;
  gross_pay: number;
  income_tax: number;
  net_pay: number;
  payout_status?: string | null;
}

export interface MyPayrollHistoryCardProps {
  history: SelfPayrollHistoryItem[];
  activeLanguage: string;
}

export const MyPayrollHistoryCard: FC<MyPayrollHistoryCardProps> = ({ history, activeLanguage }) => {
  const { t } = useTranslation();

  return (
    <article className="card list-card">
      <h2>{t('myPayrollHistory')}</h2>
      {history.length ? (
        <div className="history-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>{t('month')}</th>
                <th>{t('gross')}</th>
                <th>{t('tax')}</th>
                <th>{t('netPay')}</th>
                <th>{t('payoutStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr key={item.id}>
                  <td>{item.payroll_month || t('notAvailable')}</td>
                  <td>{formatCurrency(item.gross_pay, activeLanguage)}</td>
                  <td>{formatCurrency(item.income_tax, activeLanguage)}</td>
                  <td>{formatCurrency(item.net_pay, activeLanguage)}</td>
                  <td>{item.payout_status || t('notAvailable')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>{t('noHistory')}</p>
      )}
    </article>
  );
};

export interface SelfLoan {
  id: number | string;
  loan_amount: number;
  balance: number;
  status: string;
}

export interface MyLoansCardProps {
  loans: SelfLoan[];
  activeLanguage: string;
}

export const MyLoansCard: FC<MyLoansCardProps> = ({ loans, activeLanguage }) => {
  const { t } = useTranslation();

  return (
    <article className="card list-card">
      <h2>{t('myLoans')}</h2>
      {loans.length ? (
        <ul className="list">
          {loans.map((loan) => (
            <li key={loan.id}>
              <div>
                <strong>{formatCurrency(loan.loan_amount, activeLanguage)}</strong>
                <span>{formatCurrency(loan.balance, activeLanguage)} • {loan.status}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>{t('noLoans')}</p>
      )}
    </article>
  );
};
